import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminToken, mediaUrl } from "@/lib/cms";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 200 * 1024 * 1024;

/** Video voor een CMS-blok: uploaden (naar object storage) of een video-URL plakken. */
export const VideoField = ({ label = "Video", value = "", onChange, testId = "block-video-field" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Video is groter dan 200 MB");
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("bestand", file);
      const res = await fetch(`${BASE}/beheer/media`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken()}` },
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Uploaden mislukt");
      onChange(data.url);
      toast.success("Video geüpload");
    } catch (e) {
      toast.error(e.message || "Uploaden mislukt");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex h-20 w-28 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-secondary">
          {value ? (
            <video src={mediaUrl(value)} className="h-full w-full object-cover" muted playsInline data-testid={`${testId}-preview`} />
          ) : (
            <Film className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-[220px] flex-1 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testId}-upload-button`}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Upload className="mr-2 h-4 w-4" aria-hidden="true" />}
              {busy ? "Uploaden…" : "Video uploaden"}
            </Button>
            {value && (
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`${testId}-remove-button`}>
                <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                Verwijderen
              </Button>
            )}
          </div>
          <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/ogg,video/quicktime" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-file-input`} />
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…of plak een video-URL" data-testid={`${testId}-url-input`} />
          <p className="text-xs text-muted-foreground">MP4, WEBM of MOV · max 200 MB</p>
        </div>
      </div>
    </div>
  );
};
